import { useEffect, useState } from 'react';
import { isUpcomingRelease } from 'utils/upcomingRelease';

const PREVIEW_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/** A date on the preview banner in the viewer's language and time zone, or undefined when unknown. */
export const formatPreviewDate = (isoDate: string | undefined, locale: string | undefined): string | undefined => {
  if (!isoDate) {
    return undefined;
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  try {
    return new Intl.DateTimeFormat(locale, PREVIEW_DATE_FORMAT).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, PREVIEW_DATE_FORMAT).format(date);
  }
};

interface FormattedPreviewDateOptions {
  /** Only a date still ahead: a release date that has passed is no longer worth naming. */
  upcomingOnly?: boolean;
}

/**
 * Formats after mount: the server renders in its own time zone and at its own moment, so formatting
 * or comparing to now during render would produce different text on the client and break hydration.
 */
export const useFormattedPreviewDate = (
  isoDate: string | undefined,
  locale: string | undefined,
  { upcomingOnly = false }: FormattedPreviewDateOptions = {},
): string | undefined => {
  const [formatted, setFormatted] = useState<string>();
  useEffect(() => {
    setFormatted(upcomingOnly && !isUpcomingRelease(isoDate) ? undefined : formatPreviewDate(isoDate, locale));
  }, [isoDate, locale, upcomingOnly]);
  return formatted;
};
